/**
 * Typed error model. `ApiError` is thrown for every non-2xx response and for
 * network failures. Callers switch on `code`.
 *
 * Deliberately small: the backend's own `error.code` (tmm-api's or saas-auth's —
 * `RESELLER_ROLE_FORBIDDEN`, `SAAS_AUTH_UNAVAILABLE`, …) is preserved in
 * `details.code` rather than widening this union; a `429`'s `Retry-After` is
 * `details.retryAfterSeconds`.
 */
export type ErrorCode =
  | 'NETWORK_ERROR'
  | 'SESSION_EXPIRED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION'
  | 'CONFLICT'
  | 'RATE_LIMITED'
  | 'UNKNOWN';

export interface ApiErrorInit {
  requestId?: string;
  retryable?: boolean;
  details?: Record<string, unknown> & {
    /** Backend envelope `error.details` (e.g. VALIDATION_FAILED `{ fields }`). */
    backend?: Record<string, unknown>;
  };
}

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly requestId: string | undefined;
  readonly retryable: boolean | undefined;
  readonly details: Record<string, unknown> | undefined;

  constructor(code: ErrorCode, status: number, init: ApiErrorInit = {}) {
    super(`${code} (${status})`);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.requestId = init.requestId;
    this.retryable = init.retryable;
    this.details = init.details;
  }
}

/** Map an HTTP status to an `ErrorCode` for responses with no usable body. */
export function statusToCode(status: number): ErrorCode {
  if (status === 400) return 'VALIDATION';
  if (status === 401) return 'SESSION_EXPIRED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 429) return 'RATE_LIMITED';
  return 'UNKNOWN';
}

/** The backend envelope's own `error.code` (tmm-api or saas-auth), if the error carries one. */
export function backendCode(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  const code = err.details?.code;
  return typeof code === 'string' && code.length > 0 ? code : undefined;
}

/**
 * One string field of the backend envelope's `error.details` bag (`details.backend`, e.g. a
 * `PROMOTION_NOT_REDEEMABLE`'s `reason`). `undefined` when absent or not a string.
 */
export function backendDetail(err: unknown, key: string): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  const backend = err.details?.backend;
  if (typeof backend !== 'object' || backend === null) return undefined;
  const value = (backend as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
}

/** Seconds to wait from a `429`'s `Retry-After`, when the response carried one. */
export function retryAfterSeconds(err: unknown): number | undefined {
  if (!(err instanceof ApiError)) return undefined;
  const seconds = err.details?.retryAfterSeconds;
  return typeof seconds === 'number' && Number.isFinite(seconds) ? seconds : undefined;
}
